import React from 'react';
import './AllArticles.css';
import GoToTop from "../Support/GoToTop";

const Intercession = () => {
    return (
        <article className="article">
            <header className="header">
                <h1>Is the Intercession of the Saints Biblical?</h1>
                <p className="author">By Jakob Puckett</p>
            </header>

            <section>
                <h2>Setting Expectations from Scripture</h2>
                <p>
                    But first, we need to set our expectations of what we would anticipate finding in the Bible regarding the intercession of the saints. First, the Bible does not say much about prayers to the saints because, due to Christ’s sacrifice not yet occurring, most of them were not yet in heaven, in the presence of God.
                </p>
                <p>
                    Rather, these righteous dead are in the outer section of Hades, what Jesus refers to as “Abraham’s bosom” in one of his parables (Luke 16:19–31) and what the Old Testament repeatedly refers to as Sheol. Of course there are exceptions to this, but before the coming of Christ in the flesh, paradise was generally closed to the Old Testament saints.
                </p>
                <p>
                    These righteous dead do not cross over into paradise until Christ descends into Hades and leads them out victoriously on Bright Saturday before his resurrection (Eph. 4:8–10, 1 Peter 3:18–19, 4:6).
                </p>
            </section>

            <section>
                <h2>Old Testament Hints of Intercession</h2>
                <p>
                    There are hints in the Old Testament that reposed saints can and do intercede on behalf of the living. For instance, God mentions the intercessory powers of Moses and Samuel long after their death (Jer. 15:1). Their petition is presented as hypothetical but possible.
                </p>
                <p>
                    We know from elsewhere in the Bible that both these men are conscious, continue their mediating roles, and are concerned with earthly affairs after their physical death (1 Sam. 28:14–19, Mark 9:2–4). This is reason to assume their intercession happens.
                </p>
                <p>
                    More concretely, God says that he heard the matriarch Rachel weeping for the Israelites as they entered the Babylonian captivity, and promised to return them from exile because of Rachel’s concern for them (Jer. 31:15–17).
                </p>
                <p>
                    Scripture also mentions how some of the particularly righteous reposed saints, such as Jeremiah, pray continually to God on behalf of Israel (2 Macc. 15:12–16, Baruch 3:4).
                </p>
            </section>
            <section>
                <h2>Angelic Intercession</h2>
                <p>
                    In the Old Testament, there are beings in the presence of God who interact with and intercede for humans—these are the angels. Lot uses angels as intercessors (Gen. 19:1–2, 13–21), and Zechariah repeatedly interacts with angels interceding for Israel (Zech. 1:8–16, 3:1–5).
                </p>
                <p>
                    Job’s friends assume he could seek angelic intercession (Job 5:1, 33:23–28). Angels are appointed to assist and intercede for both individuals and nations (Deut. 32:8, Dan. 10:12–21, 12:1, Matt. 18:10, Acts 12:7–15).
                </p>
                <p>
                    Paul calls them “spirits sent forth to minister to those who will inherit salvation” (Heb. 1:14), and John sees angels carrying prayers to God (Rev. 8:3–4).
                </p>
                <p>
                    Given this background, the question we should ask is whether the building blocks are present for the early Christians to ask the saints in heaven to intercede on our behalf.
                </p>
                <p>
                    Indeed, they are.
                </p>
            </section>
            <section>
                <h2>Intercession of the Righteous</h2>
                <p>
                In the Old Testament, certain individuals found such favor with God that he accepted their prayers to affect his course of action. After Abimelech tried to take Abraham’s wife, God required him to ask Abraham to pray for him (Gen. 20:6–7). Abraham interceded for Sodom (Gen. 18:16–32). Job’s friends were told to ask Job to pray for them (Job 42:7–8).
                </p>
                <p>
                God also acted “for the sake of David” long after his death (1 Kings 11:12–34, Psalm 132:10). Moses asked God to spare Israel for the sake of Abraham, Isaac, and Jacob (Ex. 32:7–14). If these individuals held such sway even without explicit intercession, how much more powerful would their prayers be?
                </p>
                <p>
                As James writes, “The prayer of a righteous man avails much” (James 5:16).
                </p>
            </section>
            <section>
                <h2>Heavenly Awareness and Intercession</h2>
                <p>
                After Christ opened paradise to the saints, we have many reasons to believe that the saints in heaven are aware of what is happening on earth and are interceding before God. Most clearly, John in his Revelation saw the souls of martyrs underneath the golden altar in heaven, conscious and concerned with affairs on earth, crying out for God to avenge their death (Revelation 6:9–10). They must be aware of what is happening on earth to know that God had not yet done so.
                </p>
                <p>
                Similarly, in the story of Lazarus and the rich man (Luke 16:19–31), the tormented rich man is aware of and concerned for his family’s state after his death. Abraham, in his reply, is aware of how this man’s family would respond to Moses and the prophets, despite those individuals living centuries after Abraham.
                </p>
                <p>
                If this story is only a parable, we can at least say that Christ would not be theologically misleading in his stories’ contents. If this story is more than a parable, then it provides incredibly specific details on the awareness of earthly affairs by those already departed. After all, what good is it to be “surrounded by a great cloud of witnesses” (Hebrews 12:1) if the witnesses are oblivious?
                </p>
            </section>

            <section>
                <h2>Saints Alive in Christ</h2>
                <p>
                The activity and awareness of saints in heaven should be no surprise, given Christ’s constant assurances that those who believe in him will, in some way, partake of everlasting life before the final resurrection. Jesus defends the status of the righteous dead as “alive to God” (Luke 20:37–38), while Paul affirms that they are “present with the Lord” (2 Corinthians 5:8). Paul also mentions the benefits of martyrdom and being with the Lord in heaven (Philippians 1:21–23).
                </p>
            </section>

            <section>
                <h2>Saints Elevated in Christ</h2>
                <p>
                These departed saints are now on equal footing with, if not higher than, the angels themselves. Paul writes that in Christ, God became a man, not an angel, elevating mankind to the throne of God (Hebrews 1:1–13). He also asks, “Do you not know that we will judge angels?” (1 Corinthians 6:3).
                </p>
                <p>
                In Revelation, reposed humans share with angels in heavenly worship, carrying prayers to God (Revelation 5:8, 8:3–4) and serving before his throne day and night (Revelation 7:14–15). This means the prayers of the faithful already depend on their mediation to be presented to God.
                </p>
            </section>

            <section>
                <h2>Access to the Heavenly Jerusalem</h2>
                <p>
                It is to our great benefit that we already have access to the “city of the living God, the heavenly Jerusalem,” filled with angels, believers “registered in heaven,” and “the spirits of righteous men made perfect,” as well as the Father and the Son (Hebrews 12:22–24).
                </p>
                <p>
                Certainly, the incarnation of God has elevated humanity to a status it previously did not have.
                </p>
            </section>

            <section>
                <h2>Conclusion</h2>
                <p>
                We are confronted with a clear line of development from the beginning of God’s interactions with man to the final pages of Revelation. Righteous and holy men and women have always interceded powerfully. Before the incarnation, most Biblical examples show saints interceding on this side of death. Meanwhile, angels routinely intercede before God on behalf of mankind.
                </p>
                <p>
                After the incarnation, humanity is elevated to a heavenly status, sharing duties previously reserved for angels—including presenting the prayers of others to God. These saints are conscious, aware of earthly affairs, and praying before God, as part of Christ’s promise of eternal life in his presence.
                </p>
                <p>
                It is unsurprising that the earliest Christians began asking saints in heaven to intercede before God on their behalf. If “the prayer of a righteous man avails much,” why wouldn’t they?
                </p>
                <p className="author-note">
                Jakob Puckett is a reader in the Coptic Orthodox Church.
                </p>
            </section>
            <GoToTop/>
            </article>
            );
        };


export default Intercession;